import { useState, useEffect, useCallback } from 'react';
import { Card, Button, Table, Space, Typography, Modal, Input, Empty, App, Popconfirm, Tag, Tooltip, Select } from 'antd';
import { PlusOutlined, DeleteOutlined, CopyOutlined, LockOutlined, TeamOutlined, GlobalOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dashboardService from '../../api/dashboardService';
import type { Dashboard, DashboardVisibility } from './widgets/types';
import { useTheme } from '../../contexts/useTheme';
import { useAuth } from '../../hooks/useAuth';
import { ROUTES } from '../../utils/constants';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

export const DashboardsPage = () => {
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newVisibility, setNewVisibility] = useState<DashboardVisibility>('private');
  const [creating, setCreating] = useState(false);
  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [cloneSourceId, setCloneSourceId] = useState<string | null>(null);
  const [cloneTitle, setCloneTitle] = useState('');
  const [cloning, setCloning] = useState(false);
  const { theme } = useTheme();
  const { authState } = useAuth();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const currentUserId = authState.user?.id;
  const isRoot = authState.user?.role === 'root';

  const fetchDashboards = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dashboardService.listDashboards();
      if (res.success && res.data) {
        setDashboards(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch dashboards:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboards();
  }, [fetchDashboards]);

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const res = await dashboardService.createDashboard({
        title: newTitle.trim(),
        description: newDescription.trim() || undefined,
        visibility: newVisibility,
      });
      if (res.success && res.data) {
        message.success('Dashboard created');
        setCreateModalOpen(false);
        setNewTitle('');
        setNewDescription('');
        setNewVisibility('private');
        navigate(`${ROUTES.DASHBOARDS}/${res.data.dashboard_id}`);
      } else {
        message.error(res.errorData?.message || 'Failed to create dashboard');
      }
    } catch {
      message.error('Failed to create dashboard');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const res = await dashboardService.deleteDashboard(id);
      if (res.success) {
        message.success('Dashboard deleted');
        fetchDashboards();
      } else {
        message.error(res.errorData?.message || 'Failed to delete');
      }
    } catch {
      message.error('Failed to delete dashboard');
    }
  };

  const openCloneModal = (id: string, sourceTitle: string) => {
    setCloneSourceId(id);
    setCloneTitle(`${sourceTitle} (Copy)`);
    setCloneModalOpen(true);
  };

  const handleClone = async () => {
    if (!cloneSourceId || !cloneTitle.trim()) return;
    setCloning(true);
    try {
      const res = await dashboardService.cloneDashboard(cloneSourceId, cloneTitle.trim());
      if (res.success) {
        message.success('Dashboard cloned');
        setCloneModalOpen(false);
        fetchDashboards();
      } else {
        message.error(res.errorData?.message || 'Failed to clone');
      }
    } catch {
      message.error('Failed to clone dashboard');
    } finally {
      setCloning(false);
    }
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (title: string, record: Dashboard) => (
        <Button
          type="link"
          onClick={() => navigate(`${ROUTES.DASHBOARDS}/${record.dashboard_id}`)}
          style={{ padding: 0, fontWeight: 500 }}
        >
          {title}
        </Button>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (desc: string | null) => (
        <Text type="secondary" ellipsis style={{ maxWidth: 300 }}>
          {desc || '—'}
        </Text>
      ),
    },
    {
      title: 'Widgets',
      key: 'widgetCount',
      width: 80,
      render: (_: unknown, record: Dashboard) => record.widgets.length,
    },
    {
      title: 'Visibility',
      dataIndex: 'visibility',
      key: 'visibility',
      width: 120,
      render: (v: string, record: Dashboard) => {
        const isOwner = record.owner_user_id === currentUserId;
        const iconMap: Record<string, React.ReactNode> = {
          private: <LockOutlined />,
          team: <TeamOutlined />,
          template: <GlobalOutlined />,
        };
        const colorMap: Record<string, string> = { private: 'default', team: 'blue', template: 'green' };
        return (
          <Tooltip title={!isOwner ? 'Shared with you' : undefined}>
            <Tag icon={iconMap[v]} color={colorMap[v] || 'default'}>
              {v}{!isOwner && v !== 'template' ? ' (shared)' : ''}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 160,
      render: (val: string) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {val ? dayjs(val).format('MMM D, YYYY HH:mm') : '—'}
        </Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: Dashboard) => {
        const isOwner = record.owner_user_id === currentUserId;
        return (
          <Space size={4}>
            <Tooltip title="Clone">
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => openCloneModal(record.dashboard_id, record.title)}
              />
            </Tooltip>
            {isOwner && (
              <Popconfirm
                title="Delete this dashboard?"
                onConfirm={() => handleDelete(record.dashboard_id)}
                okText="Delete"
                cancelText="Cancel"
              >
                <Tooltip title="Delete">
                  <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          padding: '4px 8px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.06)',
          flexShrink: 0,
        }}
      >
        <Title level={5} style={{ margin: 0 }}>
          My Dashboards
        </Title>
        <Tooltip title="New Dashboard" placement="bottomLeft">
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalOpen(true)}
          />
        </Tooltip>
      </div>

      <Card
        style={{
          background: theme === 'dark' ? '#1f1f1f' : '#ffffff',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
          border: 'none',
        }}
        styles={{
          body: { flex: 1, display: 'flex', flexDirection: 'column', padding: '8px 12px' },
        }}
      >
        {!loading && dashboards.length === 0 ? (
          <Empty
            description="No dashboards yet"
            style={{ padding: '40px 0' }}
          >
            <Button type="primary" onClick={() => setCreateModalOpen(true)}>
              Create Your First Dashboard
            </Button>
          </Empty>
        ) : (
          <Table
            dataSource={dashboards}
            columns={columns}
            rowKey="dashboard_id"
            loading={loading}
            pagination={false}
            size="small"
          />
        )}
      </Card>

      <Modal
        title="Create New Dashboard"
        open={createModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
          setNewTitle('');
          setNewDescription('');
          setNewVisibility('private');
        }}
        onOk={handleCreate}
        confirmLoading={creating}
        okButtonProps={{ disabled: !newTitle.trim() }}
        okText="Create"
        destroyOnHidden
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>
              Title
            </Text>
            <Input
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="e.g., Race Control Overview"
              maxLength={100}
              autoFocus
              onPressEnter={() => {
                if (newTitle.trim()) handleCreate();
              }}
            />
          </div>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>
              Description (optional)
            </Text>
            <Input.TextArea
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="What is this dashboard for?"
              rows={3}
              maxLength={500}
            />
          </div>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>
              Visibility
            </Text>
            <Select<DashboardVisibility>
              value={newVisibility}
              onChange={setNewVisibility}
              style={{ width: '100%' }}
              options={[
                { value: 'private' as const, label: <span><LockOutlined style={{ marginRight: 8 }} />Private — only you</span> },
                { value: 'team' as const, label: <span><TeamOutlined style={{ marginRight: 8 }} />Team — your account members</span> },
                ...(isRoot ? [{ value: 'template' as const, label: <span><GlobalOutlined style={{ marginRight: 8 }} />Template — all users (read-only, cloneable)</span> }] : []),
              ]}
            />
          </div>
        </Space>
      </Modal>

      <Modal
        title="Clone Dashboard"
        open={cloneModalOpen}
        onCancel={() => setCloneModalOpen(false)}
        onOk={handleClone}
        confirmLoading={cloning}
        okButtonProps={{ disabled: !cloneTitle.trim() }}
        okText="Clone"
        destroyOnHidden
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>
              Name for the cloned dashboard
            </Text>
            <Input
              value={cloneTitle}
              onChange={(e) => setCloneTitle(e.target.value)}
              placeholder="Dashboard title"
              maxLength={100}
              autoFocus
              onPressEnter={() => {
                if (cloneTitle.trim()) handleClone();
              }}
            />
          </div>
        </Space>
      </Modal>
    </div>
  );
};
